import { css, html, PropertyValues, TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import {
  Color,
  DirectionalLight,
  EquirectangularReflectionMapping,
  LinearSRGBColorSpace,
  Object3D,
  Object3DEventMap,
  Texture,
  TextureLoader,
  Vector3,
} from "three";
import { EXRLoader } from "three/examples/jsm/loaders/EXRLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { EngineCamera } from "../engine/camera/Camera";
import { Engine, Entity } from "../engine/Engine";
import { BaseElement } from "../ui/base/BaseElement";
import { waitTill } from "../util/Wait";
import { logger } from "../util/Logger";

export class SugarObject extends BaseElement {
  protected renderHtml(): TemplateResult {
    return html``;
  }
  protected override useShadowRoot = false;

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("object-created", async (event: any) => {
      const object: Object3D = event.detail;
      if (this.object === event.detail) return;
      await waitTill(() => this.object != null && this.object != undefined);
      this.object?.add(object);
    });
  }

  static properties = {
    position: { type: Array },
    rotation: { type: Array },
    scale: { type: Array },
    color: { type: String },
  };
  position = [0, 0, 0];
  rotation = [0, 0, 0];
  scale = [1, 1, 1];
  color = "#ffffff";
  object?: Object3D;

  constructor() {
    super();
    this.position = [0, 0, 0];
    this.rotation = [0, 0, 0];
    this.scale = [1, 1, 1];
    this.color = "#ffffff";
  }

  createRenderRoot() {
    return this;
  }

  createObject3D() {
    this.object = new Object3D();
    return this.object;
  }

  firstUpdated(e: any) {
    super.firstUpdated(e);

    this.object = this.createObject3D();
    this.updateTransform();
    this.parentElement?.dispatchEvent(
      new CustomEvent("object-created", {
        detail: this.object,
        bubbles: true,
      }),
    );
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (this.object) {
      if (
        changedProperties.has("position") ||
        changedProperties.has("rotation") ||
        changedProperties.has("scale")
      ) {
        this.updateTransform();
      }
      if (changedProperties.has("color")) {
        //this.updateColor();
      }
    }
  }

  updateTransform() {
    if (!this.object) return;
    this.object.position.set(
      this.position[0],
      this.position[1],
      this.position[2],
    );
    this.object.rotation.set(
      this.rotation[0],
      this.rotation[1],
      this.rotation[2],
    );
    this.object.scale.set(this.scale[0], this.scale[1], this.scale[2]);
  }
}

@customElement("sugar-light")
export class SugarLight extends SugarObject {
  static properties = {
    position: { type: Array },
    rotation: { type: Array },
    scale: { type: Array },
    color: { type: String },
    intensity: { type: Number },
  };
  protected update(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    if (changedProperties.has("color") || changedProperties.has("intensity")) {
      /*const l = this.object as DirectionalLight;
      l.color = new Color(this.color);
      l.intensity = this.intensity;*/
    }
  }

  intensity = 1;

  createObject3D(): Object3D<Object3DEventMap> {
    logger.info("intensity", this.intensity);
    const l = new DirectionalLight(new Color(this.color), this.intensity);
    l.castShadow = true;
    return l;
  }
}

export class SugarElement extends SugarObject {
  protected renderHtml(): TemplateResult {
    return html``;
  }
  protected override useShadowRoot = false;

  static properties = {
    position: { type: Array },
    rotation: { type: Array },
    scale: { type: Array },
    color: { type: String },
  };
  position = [0, 0, 0];
  rotation = [0, 0, 0];
  scale = [1, 1, 1];
  color = "#ffffff";
  entity?: Entity;

  constructor() {
    super();
    this.position = [0, 0, 0];
    this.rotation = [0, 0, 0];
    this.scale = [1, 1, 1];
    this.color = "#ffffff";
  }

  createRenderRoot() {
    return this;
  }

  createEntity(): Entity | undefined {
    return undefined;
  }

  firstUpdated(e: any) {
    this.entity = this.createEntity();
    this.dispatchEvent(
      new CustomEvent("entity-created", {
        detail: this.entity,
        bubbles: true,
      }),
    );
  }
}

@customElement("sugar-canvas")
export class SugarCanvas extends SugarObject {
  static override styles = css`
    sugar-canvas {
      display: block;
      width: 100%;
      height: 100%;
      box-sizing: border-box;
    }
  `;

  protected override useShadowRoot = false;

  private _engine?: Engine;
  get engine(): Engine | undefined {
    return this._engine;
  }

  set engine(value: Engine | undefined) {
    this._engine = value;
  }

  firstUpdated(e: any): void {
    super.firstUpdated(e);
    this.object!.name = "SugarCanvas";
    setTimeout(() => {
      this.setAttribute("mobiledragover", "");
      this.setAttribute("mobiledrop", "");
      this.classList.add("center");
      this.classList.add("dropArea");

      const engine = this._engine ?? new Engine();
      engine.bindElement(this);
      if (!this.hasAttribute("no-scene-tuning")) {
        engine.setUseRenderSceneSwap(false);
        engine.setRenderSceneMergeMeshesPerMaterial(false);
        engine.setDebugLogRenderScene(false);
      }
      this._engine = engine;
      this._engine?.activeScene?.add(this.object!);

      if (!this.hasAttribute("no-environment")) {
        this.loadEnvironment(
          //"https://s3.eu-central-1.amazonaws.com/cdn.sugartech/mottobucket/hdrs/monochrome_studio_02_1k.exr",
          "https://img-sugartech.mncdn.com/cdn.sugartech/mottobucket/FFC6JX4TV8.png",
          { rotationY: Math.PI * 0.2 },
        );
      }

      if (!this.hasAttribute("no-default-camera")) {
        const activeCamera = new EngineCamera();
        activeCamera?.instance.position.set(-1.0, 0.5, 1.0);
        activeCamera?.instance.lookAt(new Vector3(0, 0, 0));
        this.engine!.activeCamera = activeCamera;
      }

      this.engine?.start();

      this.dispatchEvent(
        new CustomEvent("engine-ready", {
          detail: this._engine,
          bubbles: true,
          composed: true,
        }),
      );
    }, 10);
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.addEventListener("entity-created", async (event: any) => {
      const entity: Entity = event.detail;
      await waitTill(() => this.engine != null && this.engine != undefined);
      if (entity && this.engine) {
        this.engine.addEntity(entity);
      }
    });
  }

  private _environmentTexture?: Texture;

  private _environmentRotationY = 0;
  async loadEnvironment(
    url: string,
    options: { rotationY?: number } = {},
  ): Promise<Texture | undefined> {
    if (!url) return undefined;

    const extension = this.getFileExtension(url);
    let loader: TextureLoader | RGBELoader | EXRLoader;

    switch (extension) {
      case "exr":
        loader = new EXRLoader();
        break;
      case "hdr":
        loader = new RGBELoader();
        break;
      case "png":
      case "jpg":
      case "jpeg":
      case "webp":
        loader = new TextureLoader();
        break;
      default:
        logger.warn(
          `loadEnvironment: unknown extension "${extension}", falling back to TextureLoader`,
        );
        loader = new TextureLoader();
        break;
    }

    try {
      const texture = (await loader.loadAsync(url)) as Texture;
      texture.mapping = EquirectangularReflectionMapping;
      texture.colorSpace = LinearSRGBColorSpace;

      await waitTill(() => this.engine?.activeScene != null);
      this.engine!.activeScene!.environment = texture;
      this._environmentTexture = texture;
      Engine.activeEngine?.dispatchCustomEvent("webgl-render-scene-changed");


      return texture;
    } catch (err) {
      logger.error("loadEnvironment: failed to load", url, err);
      return undefined;
    }
  }

  setEnvironmentRotationY(radians: number): void {
    const scene = this.engine?.activeScene;
    if (!scene) return;
    scene.environmentRotation.x = radians;
    scene.backgroundRotation.x = radians;
  }

  private getFileExtension(url: string): string {
    const cleanUrl = url.split("?")[0].split("#")[0];
    const lastDot = cleanUrl.lastIndexOf(".");
    if (lastDot === -1) return "";
    return cleanUrl.substring(lastDot + 1).toLowerCase();
  }

  protected renderHtml() {
    return html``;
  }
}
